import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { CardApi } from "../../api/services";
import { errorMessage } from "../../api/client";
import { Field, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { DataTable } from "../../components/DataTable";
import { dateTime, peso } from "../../lib/format";

const STATUS_MAP: Record<string, string> = { unpaid: "pending", paid: "paid", reserved: "pending", registered: "active", void: "cancelled" };

export default function AdminCards() {
  const qc = useQueryClient();
  const [pkgId, setPkgId] = useState("");
  const [count, setCount] = useState(10);
  const [status, setStatus] = useState("");
  const { data: packages = [] } = useQuery({ queryKey: ["admin", "packages"], queryFn: CardApi.packages });
  const { data, isLoading } = useQuery({ queryKey: ["admin", "cards", status, pkgId], queryFn: () => CardApi.cards({ status: status || undefined, package_id: pkgId || undefined }) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin", "cards"] });
  const gen = useMutation({
    mutationFn: () => CardApi.generate(pkgId, count),
    onSuccess: (r: any) => { toast.success(`${r.serials.length} card serials generated (batch ${r.batch})`); refresh(); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const voidCard = useMutation({ mutationFn: (id: string) => CardApi.voidCard(id), onSuccess: refresh, onError: (e) => toast.error(errorMessage(e)) });
  const exportCsv = () => {
    const rows = [["serial", "package", "price_php", "status", "receipt", "batch"], ...(data || []).map((c) => [c.serial, c.package_name, c.price / 100, c.status, c.receipt_number || "", c.batch])];
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([rows.map((r) => r.join(",")).join("\n")], { type: "text/csv" }));
    a.download = "fastpass-cards.csv";
    a.click();
  };

  return (
    <div className="space-y-4">
      <PageHeader title="Card serial numbers" subtitle="Cards start UNPAID. A cashier must mark them PAID with a receipt number before a customer can register." />
      <div className="fp-card grid gap-3 sm:grid-cols-[2fr_1fr_auto] sm:items-end">
        <Field label="Package"><select className="fp-input" value={pkgId} onChange={(e) => setPkgId(e.target.value)} data-testid="cards-package-select">
          <option value="">All packages</option>{packages.map((p) => <option key={p.id} value={p.id}>{p.name} · {peso(p.price)}</option>)}</select></Field>
        <Field label="How many to generate"><input className="fp-input" type="number" min={1} max={500} value={count} onChange={(e) => setCount(Number(e.target.value))} data-testid="cards-count-input" /></Field>
        <button className="fp-btn-primary h-12" disabled={!pkgId || gen.isPending} onClick={() => gen.mutate()} data-testid="cards-generate-button">Generate serials</button>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <select className="fp-input max-w-[200px]" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="cards-status-filter">
          <option value="">All statuses</option><option value="unpaid">Unpaid</option><option value="paid">Paid</option><option value="reserved">Reserved</option><option value="registered">Registered</option><option value="void">Void</option>
        </select>
        <button className="fp-btn-secondary" onClick={exportCsv} data-testid="cards-export-button"><Download size={16} /> Export CSV</button>
      </div>
      {isLoading ? <Spinner /> : (
        <DataTable testId="cards-table" rows={data || []} columns={[
          { key: "serial", label: "Serial", render: (c) => <span className="font-mono font-semibold">{c.serial}</span> },
          { key: "pkg", label: "Package", render: (c) => `${c.package_name} · ${peso(c.price)}` },
          { key: "status", label: "Status", render: (c) => <StatusBadge status={STATUS_MAP[c.status]} label={c.status} /> },
          { key: "receipt", label: "Receipt / cashier", render: (c) => c.receipt_number ? <span className="text-xs">{c.receipt_number}<span className="block text-slate-500">{c.paid_by_name} · {dateTime(c.paid_at)}</span></span> : "—" },
          { key: "reg", label: "Registered", render: (c) => c.registered_at ? dateTime(c.registered_at) : "—" },
          { key: "act", label: "", render: (c) => ["unpaid", "paid", "reserved"].includes(c.status)
            ? <button className="fp-btn-secondary min-h-[36px] px-3 text-xs" onClick={() => window.confirm(`Void card ${c.serial}?`) && voidCard.mutate(c.id)} data-testid={`card-void-${c.id}`}>Void</button> : null },
        ]} />
      )}
    </div>
  );
}
